import React, { useEffect, useRef, useMemo } from 'react';
import { Calendar, MapPin, Clock, Ticket, ExternalLink, History } from 'lucide-react';

// ──────────────────────────────────────────────
// Add new events here. They auto-sort into
// "Upcoming" or "Past" based on endDate.
// ──────────────────────────────────────────────
const EVENTS = [
  {
    id: 'dire-city-002',
    title: 'DIRE CITY 002',
    subtitle: 'Drum & Bass Showcase',
    presentedBy: 'Kapital City & Dire After Dark',
    date: 'Saturday, March 14, 2026',
    endDate: '2026-03-15T00:00:00',   // ISO — used for upcoming/past logic
    time: '8:30 PM – 12:00 AM',
    venue: 'Casa de Frida',
    address: '526 Lawrence Ave, Kelowna, BC',
    artists: ['21 Realms', 'Mi$$ G', 'MVDZ'],
    price: 'CA$17.31',
    priceNote: 'Early Bird',
    eventbriteUrl: 'https://www.eventbrite.ca/e/dire-city-002-drum-bass-showcase-tickets-1983918332137?aff=oddtdtcreator',
    eventbriteId: '1983918332137',
  }
];

// ──────────────────────────────────────────────
// Upcoming Event Card (full details + checkout)
// ──────────────────────────────────────────────
const UpcomingEventCard = ({ event }) => {
  const widgetRef = useRef(null);

  useEffect(() => {
    if (!document.getElementById('eb-widgets-script')) {
      const script = document.createElement('script');
      script.id = 'eb-widgets-script';
      script.src = 'https://www.eventbrite.ca/static/widgets/eb_widgets.js';
      script.async = true;
      script.onload = () => initWidget();
      document.body.appendChild(script);
    } else if (window.EBWidgets) {
      initWidget();
    }

    function initWidget() {
      if (window.EBWidgets && widgetRef.current) {
        try {
          window.EBWidgets.createWidget({
            widgetType: 'checkout',
            eventId: event.eventbriteId,
            iframeContainerId: `eb-widget-${event.id}`,
            iframeContainerHeight: 400,
            onOrderComplete: () => {
              console.log('Ticket order complete!');
            }
          });
        } catch (e) {
          console.log('Eventbrite widget init error:', e);
        }
      }
    }
  }, [event.eventbriteId, event.id]);

  return (
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
      {/* Left — Event Info Card */}
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-br from-[var(--brand-turquoise)]/20 via-transparent to-blue-500/10 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500" />

        <div className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-[var(--brand-turquoise)] via-blue-400 to-[var(--brand-turquoise)]" />

          <div className="p-6 sm:p-8">
            <p className="text-white/50 text-xs tracking-[0.2em] uppercase mb-3 font-mono">
              {event.presentedBy} present...
            </p>

            <h3 className="text-3xl sm:text-4xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading, "Special Elite", monospace)' }}>
              {event.title}
            </h3>
            <p className="text-[var(--brand-turquoise)] text-lg font-medium mb-6">
              {event.subtitle}
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <Calendar className="text-[var(--brand-turquoise)] flex-shrink-0 mt-0.5" size={18} />
                <p className="text-white font-medium">{event.date}</p>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="text-[var(--brand-turquoise)] flex-shrink-0 mt-0.5" size={18} />
                <p className="text-white/80">{event.time}</p>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="text-[var(--brand-turquoise)] flex-shrink-0 mt-0.5" size={18} />
                <div>
                  <p className="text-white font-medium">{event.venue}</p>
                  <p className="text-white/60 text-sm">{event.address}</p>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <p className="text-white/50 text-xs tracking-[0.15em] uppercase mb-3 font-mono">Featuring</p>
              <div className="flex flex-wrap gap-3">
                {event.artists.map((artist) => (
                  <span
                    key={artist}
                    className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white text-sm font-medium hover:border-[var(--brand-turquoise)]/50 hover:bg-[var(--brand-turquoise)]/5 transition-all duration-300"
                  >
                    {artist}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div>
                <span className="text-2xl font-bold text-white">{event.price}</span>
                {event.priceNote && (
                  <span className="ml-2 text-xs px-2 py-1 bg-[var(--brand-turquoise)]/15 text-[var(--brand-turquoise)] rounded-full font-medium">
                    {event.priceNote}
                  </span>
                )}
              </div>
              <a
                href={event.eventbriteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--brand-turquoise)] text-black font-semibold rounded-lg hover:bg-[var(--brand-turquoise)]/90 transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,255,209,0.3)]"
              >
                <Ticket size={18} />
                Get Tickets
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Right — Eventbrite Checkout Widget */}
      <div className="relative">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <p className="text-white/70 text-sm font-mono tracking-wider uppercase flex items-center gap-2">
              <Ticket size={14} className="text-[var(--brand-turquoise)]" />
              Quick Checkout
            </p>
          </div>
          <div
            id={`eb-widget-${event.id}`}
            ref={widgetRef}
            className="min-h-[400px] bg-white rounded-b-2xl"
          />
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────
// Past Event Card (compact, no checkout widget)
// ──────────────────────────────────────────────
const PastEventCard = ({ event }) => (
  <div className="relative bg-[#0a0a0a] border border-white/[0.06] rounded-xl overflow-hidden opacity-70 hover:opacity-90 transition-opacity duration-300">
    <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
      {/* Date badge */}
      <div className="flex-shrink-0 text-center sm:text-left">
        <p className="text-white/40 text-xs tracking-wider uppercase font-mono">Past Event</p>
        <p className="text-white/60 text-sm font-medium mt-1">{event.date}</p>
      </div>

      {/* Divider */}
      <div className="hidden sm:block w-px h-10 bg-white/10" />

      {/* Details */}
      <div className="flex-1 min-w-0">
        <h4 className="text-lg font-bold text-white/80 truncate">
          {event.title}
          <span className="text-white/40 font-normal text-sm ml-2">— {event.subtitle}</span>
        </h4>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-white/40">
          <span className="flex items-center gap-1.5">
            <MapPin size={13} />
            {event.venue}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock size={13} />
            {event.time}
          </span>
        </div>
      </div>

      {/* Artists */}
      <div className="flex flex-wrap gap-2">
        {event.artists.map((artist) => (
          <span
            key={artist}
            className="px-3 py-1 bg-white/[0.03] border border-white/[0.06] rounded-full text-white/50 text-xs"
          >
            {artist}
          </span>
        ))}
      </div>
    </div>
  </div>
);

// ──────────────────────────────────────────────
// Main Section
// ──────────────────────────────────────────────
const EventsSection = () => {
  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    const up = [];
    const pa = [];

    for (const event of EVENTS) {
      const end = new Date(event.endDate);
      if (end > now) {
        up.push(event);
      } else {
        pa.push(event);
      }
    }

    // Upcoming: soonest first.  Past: most recent first.
    up.sort((a, b) => new Date(a.endDate) - new Date(b.endDate));
    pa.sort((a, b) => new Date(b.endDate) - new Date(a.endDate));

    return { upcoming: up, past: pa };
  }, []);

  // Hide entire section if there are no events at all
  if (EVENTS.length === 0) return null;

  return (
    <section className="py-24 sm:py-32 bg-black relative overflow-hidden" id="events">
      <div className="absolute inset-0 bg-gradient-to-b from-[var(--brand-turquoise)]/[0.02] via-transparent to-transparent pointer-events-none" />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-[7.6923%] relative z-10">
        {/* Section Header */}
        <div className="mb-12 sm:mb-16 text-center">
          <p className="section-tab">{upcoming.length > 0 ? 'Upcoming' : 'Events'}</p>
          <h2 className="section-title">Events</h2>
          <p className="body-medium text-white/60 max-w-xl mx-auto mt-4">
            {upcoming.length > 0
              ? 'Catch JLEE live at upcoming shows and events across Kelowna and beyond.'
              : 'Browse past shows and stay tuned for new event announcements.'}
          </p>
        </div>

        {/* Upcoming Events */}
        {upcoming.length > 0 && (
          <div className="space-y-16">
            {upcoming.map((event) => (
              <UpcomingEventCard key={event.id} event={event} />
            ))}
          </div>
        )}

        {/* Past Events */}
        {past.length > 0 && (
          <div className={upcoming.length > 0 ? 'mt-20' : ''}>
            {upcoming.length > 0 && (
              <div className="flex items-center gap-3 mb-6">
                <History size={16} className="text-white/30" />
                <h3 className="text-sm font-mono tracking-wider uppercase text-white/30">
                  Past Events
                </h3>
                <div className="flex-1 h-px bg-white/[0.06]" />
              </div>
            )}
            <div className="space-y-3">
              {past.map((event) => (
                <PastEventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default EventsSection;
